/**
 * Base API Client for Sira Web
 * Handles authentication, request/response, and error handling
 */
import { resolveErrorMessage } from '@/lib/utils/errors';

import { getAuthHeaders } from './authHeaders';
import { permissionsRefreshManager } from './permissionsRefresh';

interface RequestOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  params?: Record<string, any>;
  skipAuth?: boolean; // Skip authentication for public endpoints
  organizationId?: string; // Explicitly pass organization ID for this request
  accessToken?: string; // Required from server actions, where there is no browser session to read
}

class APIClient {
  private baseUrl: string;

  constructor() {
    const configured = process.env.NEXT_PUBLIC_LAUREN_URL;

    if (!configured && process.env.NODE_ENV !== 'production') {
      console.warn(
        '[apiClient] NEXT_PUBLIC_LAUREN_URL is unset; using the deployed ' +
          'backend https://sira-lauren-dev.onrender.com. Local backend ' +
          'changes will not be visible.'
      );
    }

    this.baseUrl = configured || 'https://sira-lauren-dev.onrender.com';
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(endpoint: string, params?: Record<string, any>): string {
    const url = `${this.baseUrl}${endpoint}`;

    if (!params || Object.keys(params).length === 0) {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    return `${url}?${searchParams.toString()}`;
  }

  /**
   * Make authenticated request to API
   */
  async request<T = any>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    try {
      const {
        params,
        headers: customHeaders,
        skipAuth = false,
        organizationId,
        accessToken,
        ...fetchOptions
      } = options;
      const hasBody = fetchOptions.body !== undefined;
      const isFormData = fetchOptions.body instanceof FormData;

      const authHeaders = skipAuth
        ? hasBody && !isFormData
          ? { 'Content-Type': 'application/json' }
          : {}
        : await getAuthHeaders({
            includeContentType: hasBody && !isFormData,
            organizationId,
            accessToken,
          });

      const url = this.buildUrl(endpoint, params);

      const requestHeaders = {
        ...authHeaders,
        ...customHeaders,
      };

      const response = await fetch(url, {
        ...fetchOptions,
        headers: requestHeaders,
      });

      // Handle non-JSON responses (like PDFs, CSVs, and audio)
      const contentType = response.headers.get('content-type');
      if (
        contentType?.includes('application/pdf') ||
        contentType?.includes('text/csv') ||
        contentType?.includes('audio/')
      ) {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.blob() as Promise<T>;
      }

      // Parse JSON response
      const data = await response.json();

      if (
        (data?.code === 'INSUFFICIENT_PERMISSIONS' ||
          data?.error?.code === 'INSUFFICIENT_PERMISSIONS') &&
        !endpoint.startsWith('/api/v1/permissions')
      ) {
        permissionsRefreshManager.requestRefetch();
      }

      // Handle API errors
      if (!response.ok) {
        const errorMessage = resolveErrorMessage(
          data,
          `HTTP ${response.status}: ${response.statusText}`
        );

        // Log error details including headers for debugging
        console.error(`API Error for ${endpoint}:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorMessage,
          headers: requestHeaders,
          url,
        });

        const error = new Error(errorMessage) as Error & { status?: number };
        error.status = response.status;
        throw error;
      }

      // Handle API response format
      if ('success' in data) {
        if (!data.success) {
          throw new Error(resolveErrorMessage(data, 'Request failed'));
        }
        return data.data as T;
      }

      // Return raw data if not in standard format
      return data as T;
    } catch (error) {
      // Handle specific error types
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timed out');
        }
        // Re-throw if already an Error with proper message
        throw error;
      }

      // Create a proper error message for unknown errors
      let errorMessage = 'Unknown error occurred';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        errorMessage = JSON.stringify(error);
      }

      console.error(`API Request failed for ${endpoint}:`, errorMessage);
      throw new Error(errorMessage);
    }
  }

  /**
   * GET request helper
   */
  async get<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    options?: {
      headers?: Record<string, string>;
      skipAuth?: boolean;
      organizationId?: string;
    }
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      params,
      ...options,
    });
  }

  /**
   * POST request helper
   */
  async post<T = any>(
    endpoint: string,
    data?: any,
    options?: {
      params?: Record<string, any>;
      headers?: Record<string, string>;
      skipAuth?: boolean;
      organizationId?: string;
      accessToken?: string;
    }
  ): Promise<T> {
    // Check if data is FormData - don't stringify it
    const isFormData = data instanceof FormData;

    return this.request<T>(endpoint, {
      method: 'POST',
      body:
        data !== undefined && data !== null && !isFormData
          ? JSON.stringify(data)
          : data,
      ...options,
    });
  }

  /**
   * PUT request helper
   */
  async put<T = any>(
    endpoint: string,
    data?: any,
    options?: {
      params?: Record<string, any>;
      skipAuth?: boolean;
      organizationId?: string;
    }
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  /**
   * PATCH request helper
   */
  async patch<T = any>(
    endpoint: string,
    data?: any,
    options?: {
      params?: Record<string, any>;
      skipAuth?: boolean;
      organizationId?: string;
    }
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  /**
   * DELETE request helper
   */
  async delete<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    options?: {
      skipAuth?: boolean;
      organizationId?: string;
    }
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      params,
      ...options,
    });
  }

  /** File upload with multipart/form-data. */
  async upload<T = any>(
    endpoint: string,
    formData: FormData,
    params?: Record<string, any>,
    options?: Pick<RequestOptions, 'organizationId'>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData as any,
      params,
      organizationId: options?.organizationId,
      headers: {
        // Let browser set Content-Type with boundary for multipart
      },
    });
  }
}

// Export singleton instance
export const apiClient = new APIClient();
export default apiClient;
